import { getIANATimezone, getTimezoneAbbreviation, getTimezoneSuggestions } from './timezoneUtils';

function getTzOffsetMinutes(tz, utcDate) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(utcDate);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const y = Number(get('year'));
  const m = Number(get('month'));
  const d = Number(get('day'));
  const hh = Number(get('hour'));
  const mm = Number(get('minute'));
  const ss = Number(get('second'));
  const asUTC = Date.UTC(y, m - 1, d, hh, mm, ss);
  return (asUTC - utcDate.getTime()) / 60000;
}

export function zonedDateTimeToUtcDate(dateStrYYYYMMDD, timeStrHHMMSS, timeZone) {
  if (!dateStrYYYYMMDD || !timeStrHHMMSS || !timeZone) return null;
  const [y, m, d] = String(dateStrYYYYMMDD).slice(0, 10).split('-').map(Number);
  const [hh, mm, ss] = String(timeStrHHMMSS).slice(0, 8).split(':').map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const guessUtcMs = Date.UTC(y, m - 1, d, hh, mm, ss || 0);
  const guessDate = new Date(guessUtcMs);
  const offsetMin = getTzOffsetMinutes(timeZone, guessDate);
  return new Date(guessUtcMs - offsetMin * 60000);
}

export function formatTimeInTz(utcDate, timeZone) {
  if (!utcDate || !timeZone) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(utcDate);
}

export function resolveIanaTimezone(stored) {
  if (!stored) return 'UTC';
  return getIANATimezone(stored) || stored;
}

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT_TO_DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function addDaysYmd(ymd, days) {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(Date.UTC(y, m - 1, d + Number(days)));
  return dt.toISOString().slice(0, 10);
}

export function getDayOfWeekInTz(utcDate, timeZone) {
  if (!utcDate || !timeZone) return null;
  const short = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
  }).format(utcDate);
  return WEEKDAY_SHORT_TO_DOW[String(short).slice(0, 3)] ?? null;
}

/** First calendar date at/after anchor whose teacher-local weekday matches the schedule slot. */
export function findYmdForScheduleDay(anchorYmd, dayOfWeek, teacherTz, timeHms) {
  const startYmd = anchorYmd && String(anchorYmd).slice(0, 10).match(/^\d{4}-\d{2}-\d{2}$/)
    ? String(anchorYmd).slice(0, 10)
    : '2026-01-04';
  const time = timeHms || '12:00:00';
  for (let i = 0; i < 7; i++) {
    const ymd = addDaysYmd(startYmd, i);
    const utc = zonedDateTimeToUtcDate(ymd, time, teacherTz);
    if (utc && getDayOfWeekInTz(utc, teacherTz) === Number(dayOfWeek)) return ymd;
  }
  return startYmd;
}

export function getStudentCountryTimezone(country) {
  if (!country) return null;
  const suggestions = getTimezoneSuggestions(country);
  const abbr = suggestions?.[0];
  if (!abbr) return null;
  return resolveIanaTimezone(abbr);
}

/**
 * One line per slot in the selected display timezone only, with weekday in that timezone.
 * Example: "2:30 AM GMT - MONDAY" (teacher Sunday 9:30 PM EST).
 */
export function formatScheduleTimeLines({ schedules, anchorDate, teacherTimezone, viewerTimezone }) {
  if (!schedules?.length) return [];

  const teacherTz = resolveIanaTimezone(teacherTimezone);
  const viewerTz = resolveIanaTimezone(viewerTimezone);
  const tzAbbr = getTimezoneAbbreviation(viewerTimezone || viewerTz);

  return schedules.map((schedule) => {
    const startRaw = schedule.start_time ? String(schedule.start_time).slice(0, 8) : '';
    const teacherDayOfWeek = Number(schedule.day_of_week);
    const fallbackLabel = (DAY_LABELS[teacherDayOfWeek] || `Day ${schedule.day_of_week}`).toUpperCase();
    if (!startRaw) {
      return { dayLabel: fallbackLabel, dayOfWeek: teacherDayOfWeek, line: fallbackLabel };
    }

    const slotYmd = findYmdForScheduleDay(anchorDate, teacherDayOfWeek, teacherTz, startRaw);
    const utcStart = zonedDateTimeToUtcDate(slotYmd, startRaw, teacherTz);
    if (!utcStart) {
      return { dayLabel: fallbackLabel, dayOfWeek: teacherDayOfWeek, line: fallbackLabel };
    }

    const displayDow = getDayOfWeekInTz(utcStart, viewerTz);
    const dayLabel = (DAY_LABELS[displayDow] || fallbackLabel).toUpperCase();
    return {
      dayLabel,
      dayOfWeek: displayDow ?? teacherDayOfWeek,
      line: `${formatTimeInTz(utcStart, viewerTz)} ${tzAbbr} - ${dayLabel}`,
    };
  });
}

export function formatStudentAgesDisplay(ages) {
  if (!ages?.length) return '—';
  return ages
    .map((age) => {
      const n = Number(age);
      if (Number.isNaN(n)) return null;
      return `${n} YEAR${n === 1 ? '' : 'S'}`;
    })
    .filter(Boolean)
    .join(' AND ');
}

export function formatAgeGroupCanAdd(ageGroup) {
  if (!ageGroup) return '—';
  if (String(ageGroup).endsWith('+')) return `Age ${ageGroup}`;
  const parts = String(ageGroup).split('-');
  if (parts.length === 2) {
    return `Age ${parts[0]} to ${parts[1]}`;
  }
  return `Age ${ageGroup}`;
}

export function formatBatchDaysFromTimeLines(timeLines) {
  if (!timeLines?.length) return '—';
  const seen = new Set();
  const labels = [];
  [...timeLines]
    .sort((a, b) => Number(a.dayOfWeek) - Number(b.dayOfWeek))
    .forEach((line) => {
      if (seen.has(line.dayOfWeek)) return;
      seen.add(line.dayOfWeek);
      labels.push(line.dayLabel);
    });
  return labels.length ? labels.join(' AND ') : '—';
}

export function formatBatchDaysDisplay(schedules) {
  if (!schedules?.length) return '—';
  const uniqueDays = [...new Set(schedules.map((s) => Number(s.day_of_week)))].sort((a, b) => a - b);
  return uniqueDays
    .map((d) => (DAY_LABELS[d] || `Day ${d}`).toUpperCase())
    .join(' AND ');
}
